/**
 * Calendar View
 *
 * Monthly calendar card for the homepage showing medications,
 * appointments, period tracking, and fertile window days.
 *
 * Composes shared sub-components: QuickActions, MonthlyCalendar,
 * DailyView, and useCalendarNavigation.
 */

import { useState, useMemo } from "react";
import { View, Pressable } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import { ChevronRight, Calendar } from "lucide-react-native";
import AnimatedRN, { FadeInDown } from "react-native-reanimated";
import { useCalendarNavigation } from "./useCalendarNavigation";
import { QuickActions } from "./QuickActions";
import { MonthlyCalendar } from "./MonthlyCalendar";
import { DailyView } from "./DailyView";
import { UpNextSection, LogPeriodModal } from "../components";
import type { CalendarItem } from "../calendar.types";
import { ClinicalDataStatus } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";

export interface CalendarViewProps {
  /** Animation delay in ms */
  delay?: number;
  /** Show a compact version of the calendar (hides Up Next, daily view toggle, etc.) */
  compact?: boolean;
  /** Callback when Log Period is pressed */
  onLogPeriod?: () => void;
  /** Callback when Log Medication is pressed */
  onLogMedication?: () => void;
  /** Callback when Log Appointment is pressed */
  onLogAppointment?: () => void;
  /** Whether to show the Up Next section (default: true) */
  showUpNext?: boolean;
  /** Whether to show the Log Period button (default: true) */
  showLogPeriod?: boolean;
  /** Callback when medication info is pressed in Up Next */
  onMedicationInfoPress?: (item: CalendarItem) => void;
  /** Callback when medication log is pressed in Up Next */
  onMedicationLogPress?: (item: CalendarItem) => void;
  /** Callback when appointment is pressed in Up Next */
  onAppointmentPress?: (item: CalendarItem) => void;
  /** Callback when "View calendar" is pressed (navigates to full calendar page) */
  onViewCalendar?: () => void;
  /** Hide the internal section header (when rendered externally) */
  hideHeader?: boolean;
  /** Initial view mode for the calendar (default: "monthly") */
  initialViewMode?: "monthly" | "daily";
}

export function CalendarView({
  delay = 200,
  onLogPeriod,
  onLogMedication,
  onLogAppointment,
  showUpNext = true,
  showLogPeriod = true,
  onMedicationInfoPress,
  onMedicationLogPress,
  onAppointmentPress,
  onViewCalendar,
  hideHeader = false,
  initialViewMode,
}: CalendarViewProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("CalendarView");
  // Internal modal state
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);

  // Shared calendar navigation
  const nav = useCalendarNavigation(
    initialViewMode ? { initialViewMode } : undefined,
  );

  // Get items for the selected day
  const { getItemsForDate, selectedDayString } = nav;
  const selectedDayItems = useMemo(
    () => getItemsForDate(selectedDayString),
    [getItemsForDate, selectedDayString],
  );

  return (
    <>
      <AnimatedRN.View
        entering={FadeInDown.delay(delay)}
        style={{ gap: 12, marginBottom: 24 }}
      >
        {/* Section Header */}
        {!hideHeader && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingHorizontal: 4,
            }}
          >
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
            >
              <Calendar size={14} color="#2DD4BF" strokeWidth={2} />
              <Text
                style={{
                  fontSize: 12.8,
                  fontWeight: "600",
                  color: "#CBD5E1",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Calendar
              </Text>
            </View>
            {onViewCalendar && (
              <Pressable
                onPress={() => {
                  capturePress("View calendar");
                  onViewCalendar();
                }}
                style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
              >
                <Text style={{ fontSize: 11.2, color: "#64748B" }}>
                  View calendar
                </Text>
                <ChevronRight size={12} color="#64748B" strokeWidth={2} />
              </Pressable>
            )}
          </View>
        )}

        <View className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0F172A]/60 p-5 shadow-lg">
          {/* Quick Actions */}
          <View className="relative z-20">
            <QuickActions
              onLogPeriod={onLogPeriod ?? (() => setIsPeriodModalOpen(true))}
              onLogMedication={onLogMedication}
              onLogAppointment={onLogAppointment}
              showLogPeriod={showLogPeriod}
            />
          </View>

          {/* Divider */}
          <View className="my-4 h-px bg-white/5" />

          {nav.availability === "unavailable" ? (
            <ClinicalDataStatus
              state="unavailable"
              appearance="dark"
              title={t("clinical_data.unavailable_title")}
              message={t("clinical_data.unavailable_body")}
              retryLabel={t("clinical_data.retry")}
              retryingLabel={t("clinical_data.retrying")}
              onRetry={nav.refetch}
              isRetrying={nav.isFetching}
              embedded
              testID="calendar-unavailable"
            />
          ) : (
            <>
              {nav.availability === "stale" ? (
                <ClinicalDataStatus
                  state="stale"
                  appearance="dark"
                  message={t("clinical_data.stale_body")}
                  retryLabel={t("clinical_data.retry")}
                  retryingLabel={t("clinical_data.retrying")}
                  onRetry={nav.refetch}
                  isRetrying={nav.isFetching}
                  testID="calendar-stale"
                />
              ) : null}

              {/* View Toggle */}
              <View className="relative z-20 mb-4 flex-row justify-end">
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: "rgba(255, 255, 255, 0.05)",
                    backgroundColor: "rgba(15, 23, 42, 0.4)",
                    padding: 2,
                  }}
                >
                  <Pressable
                    onPress={() => {
                      capturePress("Monthly", "calendar_view_monthly");
                      nav.setViewMode("monthly");
                    }}
                    style={{
                      borderRadius: 6,
                      paddingHorizontal: 12,
                      paddingVertical: 4,
                      backgroundColor:
                        nav.viewMode === "monthly" ? "#4ADE80" : "transparent",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 10.4,
                        fontWeight: "500",
                        color:
                          nav.viewMode === "monthly" ? "#020617" : "#64748B",
                      }}
                    >
                      Monthly
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={() => {
                      capturePress("Daily", "calendar_view_daily");
                      nav.setViewMode("daily");
                    }}
                    style={{
                      borderRadius: 6,
                      paddingHorizontal: 12,
                      paddingVertical: 4,
                      backgroundColor:
                        nav.viewMode === "daily" ? "#4ADE80" : "transparent",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 10.4,
                        fontWeight: "500",
                        color: nav.viewMode === "daily" ? "#020617" : "#64748B",
                      }}
                    >
                      Daily
                    </Text>
                  </Pressable>
                </View>
              </View>

              {/* Monthly View */}
              {nav.viewMode === "monthly" && (
                <MonthlyCalendar
                  calendarDays={nav.calendarDays}
                  displayedYear={nav.displayedYear}
                  displayedMonth={nav.displayedMonth}
                  monthYearHeader={nav.monthYearHeader}
                  isToday={nav.isToday}
                  getDaySummary={nav.getDaySummary}
                  onDayPress={nav.handleDaySelect}
                  onPreviousMonth={nav.goToPreviousMonth}
                  onNextMonth={nav.goToNextMonth}
                />
              )}

              {/* Daily View */}
              {nav.viewMode === "daily" && (
                <DailyView
                  selectedDay={nav.selectedDay}
                  items={selectedDayItems}
                  isLoading={nav.isLoading}
                  todayString={nav.todayString}
                  onPreviousDay={nav.goToPreviousDay}
                  onNextDay={nav.goToNextDay}
                />
              )}

              {/* Up Next Section */}
              {showUpNext && (
                <>
                  <View className="my-4 h-px bg-white/5" />
                  <UpNextSection
                    limit={3}
                    showHeader={true}
                    headerText="Up Next"
                    emptyMessage="No upcoming medications or appointments."
                    onMedicationInfoPress={onMedicationInfoPress}
                    onMedicationLogPress={onMedicationLogPress}
                    onAppointmentPress={onAppointmentPress}
                  />
                </>
              )}
            </>
          )}
        </View>
      </AnimatedRN.View>

      {/* Self-contained modals */}
      <LogPeriodModal
        open={isPeriodModalOpen}
        onClose={() => setIsPeriodModalOpen(false)}
      />
    </>
  );
}
